import React from 'react';
import { Box, Checkbox, Badge, Text, Tooltip, Flex } from '@mantine/core';
import { IconInfoCircle } from '@tabler/icons-react';
import { Store } from '../../../codegen/graphql';
import { isEligibleAdvertisingZoneStore } from '../../../helpers/storeSelectionHelper';
import formatCurrency from '../../../utility/formatCurrency';
import When from '../../common/When';

interface StoreSelectionItemProps {
    store: Store;
    isSelected: boolean;
    onToggle: (storeId: string, checked: boolean) => void;
    isHighestCostPrice?: boolean;
    disabled?: boolean;
}

export const StoreSelectionItem = React.memo(function StoreSelectionItem({
    store,
    isSelected,
    onToggle,
    isHighestCostPrice = false,
    disabled = false,
}: StoreSelectionItemProps) {
    const restriction = store?.priceChangeRestriction;
    const isEligible = isEligibleAdvertisingZoneStore(store);
    const costPrice = store?.productAvailability?.costPrice;
    const currency = store?.productAvailability?.currency || 'ZAR';
    const isDisabled = disabled || !isEligible;
    const showCostPrice =
        isEligible && costPrice !== undefined && costPrice !== null;
    const formattedCostPrice =
        costPrice != null ? formatCurrency(costPrice, currency) : 'N/A';

    return (
        <div
            key={store.id}
            className={`flex items-center gap-3 py-1 ${
                isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
        >
            <Checkbox
                disabled={isDisabled}
                checked={isSelected}
                onChange={event =>
                    onToggle(store.id, event.currentTarget.checked)
                }
                classNames={{
                    root: isDisabled
                        ? '!cursor-not-allowed'
                        : '!cursor-pointer',
                    body: isDisabled
                        ? '!cursor-not-allowed'
                        : '!cursor-pointer',
                    inner: isDisabled
                        ? '!cursor-not-allowed'
                        : '!cursor-pointer',
                    input: isDisabled
                        ? '!cursor-not-allowed'
                        : '!cursor-pointer',
                }}
            />
            <Badge size="sm" variant="light" color="blue">
                {store?.storeNo || 'N/A'}
            </Badge>
            <Text className="text-sm flex-1 uppercase whitespace-nowrap">
                {store?.name || 'N/A'}
            </Text>
            <When
                condition={Boolean(restriction)}
                fallback={
                    <When
                        condition={showCostPrice}
                        fallback={
                            <Badge size="sm" variant="light" color="red">
                                NOT LIVE
                            </Badge>
                        }
                    >
                        <Flex align="center" gap="xs">
                            <When condition={isHighestCostPrice}>
                                <Tooltip
                                    label="This is the highest cost price in the advertising zone"
                                    withArrow
                                    position="bottom"
                                    withinPortal
                                    floatingStrategy="fixed"
                                    zIndex={1300}
                                    multiline
                                    w={200}
                                    middlewares={{
                                        flip: true,
                                        shift: { padding: 8 },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        className="inline-flex shrink-0 cursor-help text-green-600"
                                        aria-label="Highest cost price information"
                                    >
                                        <IconInfoCircle size={14} />
                                    </Box>
                                </Tooltip>
                            </When>
                            <Text
                                className={`text-xs whitespace-nowrap ${
                                    isHighestCostPrice
                                        ? 'text-green-600 font-bold'
                                        : 'text-primary'
                                }`}
                            >
                                COST PRICE: {formattedCostPrice}
                            </Text>
                        </Flex>
                    </When>
                }
            >
                <Tooltip
                    label={`${restriction?.restrictionType}: ${restriction?.reason}`}
                    withArrow
                    position="bottom"
                    withinPortal
                    floatingStrategy="fixed"
                    zIndex={1300}
                    multiline
                    w={240}
                    middlewares={{ flip: true, shift: { padding: 8 } }}
                >
                    <Badge
                        size="sm"
                        variant="light"
                        color="orange"
                        className="cursor-help"
                    >
                        RESTRICTED
                    </Badge>
                </Tooltip>
            </When>
        </div>
    );
});
